import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ZoomIn, Folder, Images } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import Layout from '../components/Layout'
import { supabase, type GalleryImage, type GalleryAlbum } from '../lib/supabase'

export default function GalleryPage() {
  const [albums, setAlbums] = useState<GalleryAlbum[]>([])
  const [images, setImages] = useState<GalleryImage[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedAlbum, setSelectedAlbum] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null)

  useEffect(() => {
    Promise.all([
      supabase.from('gallery_albums').select('*').order('created_at', { ascending: false }),
      supabase.from('gallery_images').select('*').order('created_at', { ascending: false }),
    ]).then(([{ data: a }, { data: i }]) => {
      if (a) setAlbums(a as GalleryAlbum[])
      if (i) setImages(i as GalleryImage[])
      setLoading(false)
    })
  }, [])

  const filteredImages = selectedAlbum
    ? images.filter((img) => img.album_id === selectedAlbum)
    : images

  const selectedAlbumName = selectedAlbum
    ? albums.find((a) => a.id === selectedAlbum)?.name || 'Album'
    : null

  return (
    <Layout>
      <Helmet>
        <title>Galeria – Strefa TOP ASG i Paintball Opole | Tarnów Opolski</title>
        <meta name="description" content="Galeria zdjęć z naszego pola ASG i paintball w Tarnowie Opolskim. Wpadnij zobaczyć jak wygląda prawdziwa akcja – strzelnica ASG i paintball koło Opola." />
        <link rel="canonical" href="https://strefatop.pl/galeria" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl text-bone tracking-wider">GALERIA</h1>
          <p className="font-body text-chrome mt-4 max-w-2xl mx-auto">Chwile z pola bitwy — każde zdjęcie to historia.</p>
          <div className="w-24 h-0.5 bg-military-light mx-auto mt-6" />
        </motion.div>

        {/* Album Filter */}
        {albums.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <button onClick={() => setSelectedAlbum(null)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm font-subheading text-sm uppercase tracking-wider transition-colors ${
                selectedAlbum === null
                  ? 'bg-military-light text-bone'
                  : 'bg-[#1A1A1A]/80 border border-military/20 text-chrome hover:text-bone'
              }`}>
              <Images size={14} /> Wszystkie
            </button>
            {albums.map((album) => (
              <button key={album.id} onClick={() => setSelectedAlbum(album.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm font-subheading text-sm uppercase tracking-wider transition-colors ${
                  selectedAlbum === album.id
                    ? 'bg-military-light text-bone'
                    : 'bg-[#1A1A1A]/80 border border-military/20 text-chrome hover:text-bone'
                }`}>
                <Folder size={14} /> {album.name}
              </button>
            ))}
          </div>
        )}

        {selectedAlbumName && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center font-body text-chrome mb-6">
            Album: <span className="text-bone">{selectedAlbumName}</span> · {filteredImages.length} zdjęć
          </motion.p>
        )}

        {/* Grid */}
        {loading ? (
          <p className="text-center text-chrome font-body">Ładowanie...</p>
        ) : filteredImages.length === 0 ? (
          <p className="text-center text-chrome font-body">
            {selectedAlbum ? 'Brak zdjęć w tym albumie.' : 'Brak zdjęć w galerii.'}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence mode="popLayout">
              {filteredImages.map((img, i) => (
                <motion.div
                  key={img.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: i * 0.03 }}
                  className="group relative aspect-square overflow-hidden rounded-sm border border-military/20 cursor-pointer bg-steel"
                  onClick={() => setSelectedImage(img)}
                >
                  <img src={img.image_url} alt={img.caption}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                    <p className="font-body text-sm text-bone">{img.caption}</p>
                  </div>
                  <div className="absolute top-3 right-3 p-2 bg-military/80 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn size={16} className="text-bone" />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage.image_url} alt={selectedImage.caption} className="w-full max-h-[80vh] object-contain rounded-sm" />
            {selectedImage.caption && <p className="text-center text-bone font-body mt-4">{selectedImage.caption}</p>}
          </motion.div>
          <button onClick={() => setSelectedImage(null)} className="absolute top-6 right-6 text-bone hover:text-military-light font-display text-3xl">×</button>
        </div>
      )}
    </Layout>
  )
}
